import { ViewPlugin, Decoration, EditorView } from '@codemirror/view';
import { Transaction } from '@codemirror/state';
import { TOOLTIP_MESSAGES } from './shared/tooltipMessages.js';
import { termPositionsField, updateTermPositions } from './termPositionState.js';
import { getCommentLineRanges, overlapsCommentRange } from './commentUtils.js';

/**
 * Unified highlighter for ambiguous term errors.
 * This combines both article errors (child terms) and parent term warnings
 * into a single, consistent highlighting system.
 * 
 * Both types of errors represent the same conceptual issue:
 * - A term that appears with "a/an" instead of "the"
 * - Needs clarification whether it's a new term or reference to existing
 */

/**
 * Validates that position bounds are within document range
 */
function isValidPosition(start, end, docLength) {
    return start !== null && 
           end !== null && 
           start >= 0 && 
           end <= docLength && 
           start < end;
}

// Theme for ambiguous term highlighting - unified styling
const ambiguousTermStyles = EditorView.baseTheme({
    ".cm-ambiguous-term": {
        position: "relative",
        cursor: "default",
        // Red wavy underline for all ambiguous term errors
        textDecoration: "underline wavy var(--color-ambiguous-term-error)",
        textDecorationSkipInk: "none",
        isolation: "isolate",
        zIndex: 10,
        // Always show background highlight
        backgroundColor: "var(--color-ambiguous-term-error-bg-hover)"
    },
    ".cm-ambiguous-term:hover": {
        // Darker highlight on hover
        backgroundColor: "rgba(239, 68, 68, 0.25)"
    }
});

// Create the unified ambiguous term highlighter plugin
function createAmbiguousTermHighlighter(termsStore) {
    let currentStore = { 
        termEntries: new Map(),
        claimsModifiedSinceHeavyRefresh: false
    };
    
    const plugin = ViewPlugin.fromClass(class {
        constructor(view) {
            this.view = view;
            this.decorations = this.computeDecorations(view);
            this.waitingForFreshPositions = false;
            this.lastPositionsSnapshot = null;
            
            this.unsubscribe = termsStore.subscribe(store => {
                currentStore = {
                    termEntries: store.termEntries || new Map(),
                    claimsModifiedSinceHeavyRefresh: store.claimsModifiedSinceHeavyRefresh || false
                };
                const termEntries = currentStore.termEntries;
                
                // Don't update term positions when claims have been modified since last heavy refresh
                // This prevents position mapping errors when positions are stale
                if (currentStore.claimsModifiedSinceHeavyRefresh) {
                    return;
                }
                
                if (this.waitingForFreshPositions) {
                    const currentSnapshot = this.createPositionsSnapshot(termEntries);
                    
                    if (this.lastPositionsSnapshot && currentSnapshot !== this.lastPositionsSnapshot) {
                        this.waitingForFreshPositions = false;
                        this.lastPositionsSnapshot = currentSnapshot;
                        if (termEntries.size > 0) {
                            updateTermPositions(this.view, termEntries);
                        }
                    } else {
                        return;
                    }
                } else {
                    this.lastPositionsSnapshot = this.createPositionsSnapshot(termEntries);
                    if (termEntries.size > 0) {
                        updateTermPositions(this.view, termEntries);
                    }
                }
            });
        }
        
        createPositionsSnapshot(termEntries) {
            // Create a snapshot of all ambiguous term positions for comparison
            const positions = [];
            termEntries.forEach((termData) => {
                // Article errors from child terms
                if (termData.children) {
                    Object.entries(termData.children).forEach(([_childTerm, childData]) => {
                        if (childData.references?.abs_start_chars && childData.references?.errors) {
                            childData.references.errors.forEach((error, i) => {
                                if (error) {
                                    positions.push(`${childData.references.abs_start_chars[i]}-${childData.references.abs_end_chars[i]}`);
                                }
                            });
                        }
                    });
                }
                // Parent term warnings
                if (termData._warning?.type === 'prefix_warning' && termData.abs_start_char !== undefined) {
                    positions.push(`${termData.abs_start_char}-${termData.abs_end_char}`);
                }
            });
            return positions.sort().join(',');
        }

        update(update) {
            const isClaimDepFix = update.transactions.some(tr => 
                tr.annotation(Transaction.userEvent) === 'claim-dependency.accept'
            );
            
            if (isClaimDepFix) {
                this.decorations = Decoration.none;
                this.waitingForFreshPositions = true;
                return;
            }
            
            const positionsChanged = update.state.field(termPositionsField) !== update.startState.field(termPositionsField);
            
            if (this.waitingForFreshPositions) {
                return;
            }
            
            if (update.docChanged || update.viewportChanged || positionsChanged) {
                this.decorations = this.computeDecorations(update.view);
            }
        }

        computeDecorations(view) {
            const termPositions = view.state.field(termPositionsField);
            if (!termPositions || termPositions.positions.size === 0) {
                return Decoration.none;
            }

            // Don't show error highlights when claims have been modified since last heavy refresh
            // This prevents misaligned error highlights when positions are stale
            if (currentStore.claimsModifiedSinceHeavyRefresh) {
                console.log('[CodeMirror] Skipping error highlights - claims modified since heavy refresh');
                return Decoration.none;
            }

            const builder = [];
            const text = view.state.doc.toString();
            if (!text) return Decoration.none;
            
            // Get comment ranges to exclude from highlighting
            const commentRanges = getCommentLineRanges(view);
            
            // Collect all error ranges first
            const errorRanges = [];

            termPositions.positions.forEach((posArray, _termKey) => {
                posArray.forEach(pos => {
                    const { from, to, data } = pos;
                    let shouldHighlight = false;
                    let adjustedFrom = from;
                    let term = data.term;
                    let source = '';
                    
                    // Check if this is an article error (child term)
                    if (data.type === 'article_error') {
                        shouldHighlight = true;
                        source = 'child_term';
                        // Article errors already have adjusted positions
                    }
                    // Check if this is a parent term warning
                    else if (data.type === 'parent' && data.warning?.type === 'prefix_warning') {
                        shouldHighlight = true;
                        source = 'parent_term';
                        // Adjust position if warning indicates article offset
                        if (data.warning.article_offset) {
                            adjustedFrom += data.warning.article_offset;
                        }
                    }
                    
                    if (shouldHighlight && isValidPosition(adjustedFrom, to, text.length) &&
                        !overlapsCommentRange(adjustedFrom, to, commentRanges)) {
                        errorRanges.push({
                            from: adjustedFrom,
                            to: to,
                            term: term,
                            source: source,
                            data: data
                        });
                    }
                });
            });
            
            // Merge overlapping or adjacent ranges
            const mergedRanges = [];
            errorRanges.sort((a, b) => a.from - b.from);
            
            errorRanges.forEach(range => {
                if (mergedRanges.length === 0) {
                    mergedRanges.push(range);
                } else {
                    const lastRange = mergedRanges[mergedRanges.length - 1];
                    // Check if ranges overlap or are adjacent
                    if (range.from <= lastRange.to) {
                        // Merge ranges - extend to encompass both
                        lastRange.to = Math.max(lastRange.to, range.to);
                        // Combine terms for the tooltip
                        if (!lastRange.term.includes(range.term)) {
                            lastRange.term += ', ' + range.term;
                        }
                    } else {
                        mergedRanges.push(range);
                    }
                }
            });
            
            // Create decorations for merged ranges
            mergedRanges.forEach(range => {
                const decoration = Decoration.mark({
                    class: "cm-ambiguous-term",
                    attributes: {
                        'data-error-type': 'ambiguous_term',
                        'data-message': TOOLTIP_MESSAGES.AMBIGUOUS_TERM_ERROR,
                        'data-term': range.term,
                        'data-expected': range.data?.expected || `the ${range.term}`,
                        'data-source': range.source, // For debugging
                        'data-context': range.data?.error?.context || range.data?.warning?.message || 
                                      `Found: "${range.term}" | Expected: "the ${range.term}" or more specific term`
                    }
                });
                
                builder.push(decoration.range(range.from, range.to));
            });
            
            return Decoration.set(builder.sort((a, b) => a.from - b.from));
        }
        
        destroy() {
            if (this.unsubscribe) {
                this.unsubscribe();
            }
        }
    }, {
        decorations: v => v.decorations
    });
    
    return [
        termPositionsField, // Include the StateField
        plugin,
        ambiguousTermStyles
    ];
}

export { createAmbiguousTermHighlighter, ambiguousTermStyles };